import { useState, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import type { PhotoDto, PhotoRequest, SortablePhoto } from '../types/apartment';

interface UsePhotoCarouselOptions {
  initialPhotos: PhotoDto[];
  onOrderChange: (photos: PhotoRequest[]) => void;
}

interface UsePhotoCarouselReturn {
  photos: SortablePhoto[];
  activeId: string | null;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
  handleMarkAsPrimary: (key: string) => void;
}

/** Converts a PhotoDto array to SortablePhoto[], sorted ascending by displayOrder.
 *  Uses array index as fallback when displayOrder is absent. */
function toSortablePhotos(photos: PhotoDto[]): SortablePhoto[] {
  return [...photos]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((photo, index) => ({
      key: photo.key,
      photoUrl: photo.photoUrl,
      // Fall back to photoUrl when thumbnailUrl is absent so img src is never undefined
      thumbnailUrl: photo.thumbnailUrl ?? photo.photoUrl,
      optimizedUrl: photo.optimizedUrl,
      uploadedAt: photo.uploadedAt,
      displayOrder: index,
    }));
}

/** Converts a SortablePhoto array to the PhotoRequest write shape expected by the API. */
function toPhotoRequests(photos: SortablePhoto[]): PhotoRequest[] {
  return photos.map(p => ({
    key: p.key,
    photoUrl: p.photoUrl,
    thumbnailUrl: p.thumbnailUrl,
    optimizedUrl: p.optimizedUrl,
    uploadedAt: p.uploadedAt,
    displayOrder: p.displayOrder,
  }));
}

/**
 * Manages local photo ordering state, drag events, and mark-as-primary logic for
 * the photo gallery editor. Calls onOrderChange after every reorder or primary change.
 *
 * Invariants upheld after every mutation:
 *  - Exactly one photo has displayOrder === 0.
 *  - displayOrder values form a dense sequence: 0, 1, 2, …
 *  - onOrderChange is never called when the order is unchanged.
 */
export function usePhotoCarousel({
  initialPhotos,
  onOrderChange,
}: UsePhotoCarouselOptions): UsePhotoCarouselReturn {
  const [photos, setPhotos] = useState<SortablePhoto[]>(() =>
    toSortablePhotos(initialPhotos)
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sync carousel state when photos are added or removed externally (e.g. after
  // a successful upload or deletion). We skip the sync while a drag is in
  // progress to avoid resetting mid-drag state.
  useEffect(() => {
    if (activeId !== null) return; // don't interrupt an active drag
    setPhotos(toSortablePhotos(initialPhotos));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhotos]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setPhotos(prev => {
      const oldIndex = prev.findIndex(p => p.key === String(active.id));
      const newIndex = prev.findIndex(p => p.key === String(over.id));

      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(prev, oldIndex, newIndex).map((p, i) => ({
        ...p,
        displayOrder: i,
      }));

      onOrderChange(toPhotoRequests(reordered));
      return reordered;
    });
  };

  const handleMarkAsPrimary = (key: string) => {
    setPhotos(prev => {
      const target = prev.find(p => p.key === key);
      // No-op: already primary
      if (!target || target.displayOrder === 0) return prev;

      // Assign displayOrder=0 to the target, then re-number the rest
      // preserving their existing relative order.
      const others = prev
        .filter(p => p.key !== key)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((p, i) => ({ ...p, displayOrder: i + 1 }));

      const updated: SortablePhoto[] = [{ ...target, displayOrder: 0 }, ...others];

      onOrderChange(toPhotoRequests(updated));
      return updated;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return { photos, activeId, handleDragStart, handleDragEnd, handleDragCancel, handleMarkAsPrimary };
}
